import { PresentationTone } from "../../types.ts";
import type { LocalizationSettings } from "./types.ts";

const en: LocalizationSettings = {
	key: "en",
	languageLabel: "English",
	languageNameForGeneration: "English",
	switchLabel: "Language settings",
	rotatePrompt: "Please turn your device to view the presentation",
	overview: {
		title: "AI Slides",
		tagline: "Let Artificial Intelligence create your presentations!",
		newPresentation: "Start creating",
	},
	newPresentation: {
		createPresentationTitle: "Define the topic of your presentation.",
		presentationTitle: "Think of a topic / title for your presentation",
		toneErnest: "serious",
		toneFunny: "fun",
		createButton: "Use prompt & continue",
		presentationStyle: "Presentation style",
		topic: "about ‘{topic}’",
		backToLastPresentation: "Go back to the presentations",
		backToOverview: "Go back to the overview",
		presenterTitle: "Presenter",
		clearPresenter: "Remove",
		chooseMode: "Please choose.",
		chooseModeInfoMessage:
			"Choose your preferred mode. You can always switch back to simple mode in the process. Try it now!",
		simpleMode: {
			title: "Simple Mode",
			description:
				"Continue with your prompt to generate the presentation with a general voice and look.",
		},
		advancedMode: {
			title: "Advanced Mode",
			description: "Personalize your presentation with generative AI.",
		},
		dataPrivacy: {
			title: "Information about Data Privacy.",
			text:
				"We process images, sounds, and other content in the EU and the US with strict security measures, including encryption and access controls, to ensure your data is kept secure and not used for training purposes.\n\n" +
				"Generated content is deleted immediately after generation and once you leave presentation mode.\n\n" +
				"Enjoy the latest technologies{location} while we prioritize your security and comply with all relevant data protection laws.\n\n" +
				"For further information, please visit {privacyUrl}.",
			simple: "Simple Mode",
			accept: "Accept & continue",
			futureLab: " in our Future Lab",
		},
		photo: {
			takeTitle: "Take a photo.",
			takeButton: "Start 3 second timer",
			takeError: "The picture could not be taken due to a technical error.",
			goToSimple: "Create a simple presentation instead",
			takeInfoDialog:
				"Your picture is used to generate an avatar based on your look. The avatar will be the moderator of your presentation.",
			verifyTitle: "Photo ok?",
			retake: "Take new picture",
			accept: "Accept & continue",
		},
		audio: {
			recordTitle: "Record your audio.",
			recordInfo:
				"Your recording of the text below is used to generate the voice over of the presentation. We suggest to talk clearly in an engaging way to achieve the best result.",
			recordContent:
				"Early in the morning, the city awakens to the sound of busy streets and cheerful birds. People hurry to work, drink coffee, and greet each other with a smile. In the park, dogs run freely, chasing their favorite toys. The air is fresh, filled with the scent of blooming flowers and freshly baked bread from the nearby bakery. Life in the city pulses, and every corner offers a new story to discover.",
			recordContentVariant:
				'The little lamb Wooly hops happily across the meadow. Wooly keeps sniffing the flowers and herbs. She finds sunflowers particularly beautiful. Wooly would also like to nibble on one of the sunflowers, but unfortunately, she is much too small. She wishes she could fly like the butterflies or the bees. Then she could finally try a sunflower. How would it taste? Wooly is lost in thought and doesn\'t notice that all the other sheep have long since moved on. Then she hears her mother\'s bleating and runs to her as fast as she can. "Were you daydreaming again?" her mother asks. "Yes," Wooly replies, "about sunflowers." "That suits your sunny disposition," says her mother.',
			startRecording: "Start recording",
			finishRecording: "Finish recording",
			infoDialog:
				"Please read the text on the screen aloud. It will be used to generate the voiceover of your presentation. Start when you’re ready.",
			error: "The audio recording could not be saved due to a technical error.",
			verifyTitle: "Recording finished.",
			verifyInfo:
				"You can choose to record a new audio if you e.g. accidentally stopped the audio too early. Don’t worry about it too much. ",
			verifyContent:
				"Pauses or other small interferences in the audio do not affect the result. However, if there are major background noises you can record a new audio to achieve the best possible outcome.",
			retake: "Record new audio",
			accept: "Accept & continue",
		},
		generating: {
			loading: "Loading your presentation",
			learnMore: "Learn more while you're waiting",
			infoDialog:
				"While we are generating your presentation, learn more about this showcase by navigating through the screens.",
		},
		cancel: {
			title: "Cancel advanced process?",
			backHome: "Back to home",
			simple: "Switch to simple",
			continue: "Continue advanced",
			infoDialog:
				"Be careful! If you switch to simple mode or go back to home, your progress in the advanced mode will be lost.",
		},
	},
	newStory: {
		createStory: "Which story should I tell?",
		storyReady: "Done! Would you like me to read you the story?",
		creationFailed: "Oops, something went wrong.",
		titleLabel: "Your title",
		submitButtonText: "Let's go",
		readOutButtonText: "Read out",
		retryButtonText: "Try again",
	},
	presentationReady: {
		goToPresentation: "Start watching",
		title: "Your presentation is ready!",
		infoDialog: "Watch your presentation on the screen above.",
	},
	generationFailed: {
		headline: "Technical error.",
		timeoutHeadline: "Loading timeout.",
		spicyHeadline:
			"The given topic was detected to be potentially inappropriate",
		retryPrompt: "Try again",
		restartPrompt: "Start with prompt",
		quotaExceeded: "The limit for the number of presentations was reached",
		info: "A technical error occurred. This can happen e.g. when the AI systems are not available. Please just try again.",
		timeoutInfo:
			"Unfortunately, your presentation reached the maximum loading time of 5 minutes. This can be due to the server being overutilized.",
		spicyInfo:
			"We automatically moderate your prompt. This is necessary to comply with the terms of use of the AI services.",
		quotaExceededInfo:
			"Please inform a staff member that the limit for the number of presentations was reached.",
	},
	presentation: {
		jumpToOverview: "Back to overview",
		jumpToNext: "Jump to the next presentation",
		createYourOwn: "Create your own presentation",
		confirmTitle: "Presentation completed",
		confirmNext: "To the next presentation",
		replay: "Play again",
		confirmCreateOwn: "Do you want to create your own presentation?",
		confirmCreateOwnAction: "Create my own presentation",
		declineCreateOwnAction: "Continue viewing this presentation",
		title: "Presentation is playing",
		cancelPrompt: "Do you really want to cancel?",
		finished: "Presentation finished!",
		answerQuestionFailed: "The answer could not be generated",
		qrLabel: "View your presentation at home",
		preserve: "Keep presentation for one month",
		delete: "Delete this presentation",
		actions: {
			cancel: "Cancel presentation",
			continue: "Continue watching",
			pause: "Pause",
			play: "Play",
			toOverview: "Back to start",
			replay: "Watch again",
			toggleSubtitlesOn: "Turn on subtitles",
			toggleSubtitlesOff: "Turn off subtitles",
			askQuestion: "Press and hold to ask a question",
		},
	},
	story: {
		childBookTitle: "The automatic story teller",
		darkTalesTitle: "Dark Tales",
		christmasTitle: "The mechanical Santa",
		hearAgainButtonText: "Read once again",
		backButtonText: "Back to the overview",
		playButtonText: "Play",
	},
	info: {
		aiSlides: {
			title: "About AI Slides",
			text: `
AI-Slides is an AI-based tool that *transforms a user's input into a complete presentation*. Multiple AI services work together in the generation pipeline.

In this setup, multiple AI services work together in the generation pipeline. GPT-5.5 creates slide text and image prompts, Google Nano Banana Pro generates images, ElevenLabs creates speech, and D-ID is used when a speaking video is generated. Together, these systems create the text, visuals, narration, and optional presenter animation for the final presentation.

Once everything is done - the audio tracks, the images, and the slide text - they are integrated to *form a finished, AI-composed presentation*. This can then be viewed in a web browser, offering a user-friendly way to quickly generate presentations with just a prompt.
            `,
		},
		gpt: {
			title: "GPT-5.5",
			text: `
GPT-5.5 is the language model used for creating the slide structure, slide text, narration, and image descriptions. It receives the topic and presentation settings, then produces the content that the other generation services use.

Like other large language models, GPT-5.5 predicts useful continuations from the prompt and the previous context. In AI Slides, those predictions become a coherent presentation outline, speaker text, and prompts for image generation.
            `,
		},
		nanoBanana: {
			title: "Nano Banana Pro",
			text: `
Google Nano Banana Pro is used through Replicate to generate presentation images from the image descriptions produced by the text model.

Nano Banana Pro turns text prompts into images by interpreting the requested scene, style, and visual details. These generated images are then combined with the slide content to create visually rich presentation slides.
            `,
		},
		did: {
			title: "D-ID",
			text: `
When a speaking presenter video is requested, D-ID animates a still portrait so that it appears to speak the generated narration.

D-ID combines a portrait image with an audio track and produces a realistic talking-head video. If no presenter video is requested, the presentation can be generated without this step.
            `,
		},
		elevenlabs: {
			title: "ElevenLabs",
			text: `
ElevenLabs generates human-like speech from the narration text. The generator uses ElevenLabs voices for preset presenters and can also use a provided voice sample when a custom presenter is created.

The resulting audio is used directly as narration and, when D-ID is active, as the speech track for the animated presenter video.
            `,
		},
	},
	record: {
		startRecording: "Start recording",
		removeRecording: "Remove recording",
		portrait: "Portrait image",
		takeSnapshotHeader: "Take a snapshot",
		takeSnapshot: "Take snapshot",
		acceptSnapshotHeader: "Accept snapshot",
		acceptSnapshot: "Accept snapshot",
		retakeImage: "Retake image",
		audioRecordingHeader: "Record audio",
		readText:
			"Early in the morning, the city wakes up to the sound of busy streets and cheerful birds. People rush to their work, sipping coffee and greeting each other with a smile. In the park, dogs run freely, chasing after their favorite toys. The air is fresh, filled with the aroma of blooming flowers and freshly baked bread from the nearby bakery. Life in the city is vibrant, with every corner offering a new story to discover.",
		startAudioRecording: "Start audio recording",
		stopAudioRecording: "Stop audio recording",
		acceptAudioHeader: "Accept audio recording",
		acceptAudioRecording: "Accept audio recording",
		retakeAudioRecording: "Retake audio recording",
		cancel: "Cancel",
	},
	upload: {
		dragDropImage: "Drag and drop an image here, or click to select",
		dragDropAudio: "Drag and drop an audio file here, or click to select",
		altImage: "Portrait",
		audioNotSupported: "Your browser does not support audio",
	},
	readInfo: "Read",
	pauseReading: "Pause",
	generatorMessages: {
		[PresentationTone.FUNNY]: {
			initial: "Firing up the AI brain for some slide deck magic.",
			messages: [
				"Feeding data into the AI, let's see what it can cook up.",
				"Pulling topics out of the virtual hat for your slide deck.",
				"Judging the relevance of topics, not all ideas are gold.",
				"Flexing the AI's vocabulary muscles for text generation.",
				"Crafting your slide deck outline, like a fine artisan.",
				"Playing around with colors, every slide deck needs a little splash.",
				"First draft done - not perfect, but it's a start.",
				"Turning each slide into a mini masterpiece.",
				"Flipping through the dictionary for the perfect words.",
				"Summoning the power of Picasso for some image placing.",
				"Unleashing the grammar police, courtesy of deep learning.",
				"Running a text-to-common-sense test, keeping it real.",
				"Curating slide order for a jaw-dropping narrative.",
				"Fixing visuals like a pro - clarity is king.",
				"Creating infographics, making numbers fun again.",
				"Bridging slides with smooth transitions, just like a pro skater.",
				"Thinking up a catchy intro, first impressions matter.",
				"Giving the text a readability makeover, out with the complex!",
				"Weaving in audience attention-grabbers, because who doesn't love interaction?",
				"Optimizing slide deck for big screens, small screens, and screens in-between.",
				"Polishing the outline, because we’re all about that shape.",
				"Spinning words into magic, because detail is key.",
				"Jazzing up the text design, legibility never looked so good.",
				"Designing closing slide, the grand finale.",
				"Calling in the virtual inspector gadget for a final visual check.",
				"Playing Tetris with text and visuals, until it fits just right.",
				"Bringing in insightful nuggets, straight from the AI oven.",
				"Scanning sentiment, ensuring the slide deck doesn't hurt feelings.",
				"Making sure our slides are on the same page.",
				"Turning the visual appeal knob up to 11.",
				"Locking in the color scheme, because who decides on the first try?",
				"Double-checking image resolutions, no room for pixel play.",
				"Invoking the power of language processing, hello smart text.",
				"Cooking up a slide deck title that would make Spielberg jealous.",
				"Checking infographic legibility, making sure even grandpa can read it.",
				"Shuffling text sizes and fonts, for the love of style.",
				"Breathing context into images, every picture tells a story.",
				"Timing slides to perfection, not too fast, not too slow.",
				"Trimming the slide deck size, keeping it travel-friendly.",
				"Sit back, final review in progress, almost there...",
				"Testing slide deck structure for a smooth narrative flow.",
				"One last sprinkling of design magic.",
				"Performing last minute detective work for coherence, no mystery unsolved.",
				"Polishing the slide deck for the grand reveal.",
				"Distilling the essence of the slide deck into a killer summary.",
				"Crossing the finish line on design and content.",
				"Exporting your sparkly slide deck, in all its glory.",
				"Tada! Your slide deck is ready for its standing ovation.",
				"Putting a bow on the AI’s work, slide deck check.",
				"Who’s up for a victory dance? Slide deck creation, check.",
				"Shutting down the AI engine, it’s earned a good sleep.",
			],
		},
		[PresentationTone.ERNEST]: {
			initial: "Initialising AI system for slide deck creation.",
			messages: [
				"Preprocessing raw data for insights generation.",
				"Creating a list of potential topics for the slide deck.",
				"Validating the relevance of the generated topics.",
				"Initiating generation of contextual text for each topic.",
				"Creating a slide deck outline based on topic analysis.",
				"Designing color scheme for the slide deck.",
				"Creating the first draft of the slide deck design.",
				"Initiating the design of individual slides.",
				"Generating text for individual slides.",
				"Incorporating images and visual elements into slides.",
				"Engaging deep learning algorithms for proofreading text.",
				"Conducting a coherence check on the generated text.",
				"Arranging slides based on the information hierarchy.",
				"Fine-tuning visual elements for optimal clarity.",
				"Generating infographic elements for data representation.",
				"Establishing links between slides for smooth transitions.",
				"Initiating the introduction slide creation.",
				"Enhancing the readability of the slide deck text.",
				"Incorporating audience engagement elements.",
				"Optimizing slide deck for mobile and desktop viewing.",
				"Fine-tuning the outline of the slide deck.",
				"Generating grammatically correct and insightful sentences.",
				"Refining the design of the text for better visibility.",
				"Creating a professional and aesthetically pleasing closing slide.",
				"Running a final check on all visual elements in the slide deck.",
				"Ensuring text and visuals are well-aligned in each slide.",
				"Incorporating AI generated insights into relevant slides.",
				"Deploying machine learning for sentiment analysis of text.",
				"Inspecting the consistency between slides.",
				"Boosting the visual appeal of the slide deck.",
				"Finalizing the color scheme of the slide deck.",
				"Ensuring resolution of all images is optimal.",
				"Deploying natural language processing to enhance the textual content.",
				"Creating a compelling slide deck title.",
				"Inspecting the readability of infographic elements.",
				"Modifying text size and fonts for better legibility.",
				"Adding context to images and visual elements.",
				"Ensuring all slides meet the designated presentation time.",
				"Optimizing overall slide deck size for easy sharing.",
				"Conducting a final review for potential improvements.",
				"Analyzing the structure of the slide deck for logic flow.",
				"Finalizing the content and design of the slide deck.",
				"Running the last coherence check on the slide deck.",
				"Polishing the slide deck for final delivery.",
				"Creating a summary of the slide deck content.",
				"Completing the design and content creation process.",
				"Exporting the finished slide deck in the preferred format.",
				"Slide deck is ready for review and feedback.",
				"AI system is finalizing the slide deck.",
				"The AI-based slide deck creation is complete.",
				"Initiating the deactivation of the slide deck creation algorithm.",
				"AI system shut down after successful creation of slide deck.",
			],
		},
	},
	keyboardLayout: {
		default: [
			"1 2 3 4 5 6 7 8 9 0 - = {bksp}",
			"q w e r t y u i o p [ ] !",
			"a s d f g h j k l ; ?",
			"{shift} z x c v b n m , . / {shift}",
			"@ {space}",
		],
		shift: [
			"~ ! @ # $ % ^ & * ( ) _ + {bksp}",
			"Q W E R T Y U I O P { } !",
			"A S D F G H J K L : ?",
			"{shift} Z X C V B N M < > ? {shift}",
			"@ {space}",
		],
	},
	keyboardLabels: {
		"{bksp}": "⌫",
		"{shift}": "⇧ shift",
		"{space}": "space",
	},
	landingpage: {
		login: {
			login: "Login",
			logout: "Logout",
			username: "Username",
			password: "Password",
			changePassword: "Change Password",
			newPassword: "New Password",
			repeatNewPassword: "Repeat",
			close: "Close",
			saveNewPassword: "Save new password",
		},
		menu: {
			home: "Home",
			suggestTopic: "Suggest a Topic",
			reviewSuggestions: "Review Suggestions",
			newPresentation: "New Presentation",
			newStory: "New Children’s Book",
			newDarkTale: "New Dark Tale",
			newChristmasTale: "New Christmas Story",
		},
		footer: {
			imprint: "Imprint",
			privacy: "Privacy Statement",
			language: "Change language",
		},
		presentations: {
			favorite: "favorite",
			unfavorite: "remove from favorites",
			delete: "delete",
			unpublish: "unpublish",
			publish: "Publish",
			failedToDelete: "failed to delete presentation",
			failedToUnpublish: "failed to unpublish presentation",
		},
		suggestTopic: {
			title: "Suggest a presentation topic",
			yourSuggestion: "your suggestion",
			submitSuggestion: "submit suggestion",
			suggestionSubmitted: "Your suggestion has been submitted",
			suggestAnother: "make another suggestion",
			submissionFailed: "Sorry, we could not save your submission",
			retry: "try again",
		},
		manageSuggestions: {
			title: "Manage suggestions",
			delete: "delete suggestion",
		},
		newPresentation: {
			title: "New Presentation",
			topic: "Topic",
			generateTopic: "Generate topic",
			useSuggestion: "Use a suggestion",
			numberOfSlides: "Number of slides",
			createPresentation: "Create Presentation",
			persona: "Presenter",
			personaChange: "Change",
			personaModalTitle: "Choose a presenter",
			imageGenerator: "Image generator",
			speakerVideoGenerator: "Speaker video generator",
			textGenerator: "Text generator",
			loadingOptions: "Loading options…",
			loadingOptionsError: "Failed to load presentation options",
			generateTopicFailed: "Failed to generate topic",
			loadSuggestionsFailed: "Failed to load suggestions",
			noSuggestions: "No suggestions available yet",
			cancel: "Cancel",
			useSelectedSuggestion: "Use",
			generationFailed: "Generation has failed",
			generationFailedTechnicalError: "Technical Error",
			generationDenied: "Generation was denied",
			retryWithSamePrompt: "Try again?",
			retryWithDifferentPrompt: "Try with a different prompt",
			customPersona: {
				createTile: "+ Create custom",
				customTileLabel: "Custom",
				discardCustom: "Discard custom persona",
				formTitle: "Create custom persona",
				nameLabel: "Name",
				labelLabel: "Short label",
				personalityLabel: "Personality",
				voiceSectionLabel: "Voice",
				voiceModePreset: "Pick a preset voice",
				voiceModeClone: "Clone my voice from a sample",
				voicePresetSelectLabel: "Voice",
				voicePresetsUnavailable: "No preset voices available right now.",
				voiceSampleHint:
					"Upload or record at least 10 seconds of clear speech.",
				imageSectionLabel: "Speaker image (optional)",
				imageOptional:
					"Used as the persona's avatar and, if available, for the D-ID speaker video.",
				back: "Back",
				saveAndUse: "Save & use",
				uploadOrRecordUploadTab: "Upload",
				uploadOrRecordRecordTab: "Record",
				uploadOrRecordDropImage:
					"Drop an image here, or click to choose a file.",
				uploadOrRecordDropAudio:
					"Drop an audio file here, or click to choose a file.",
				uploadOrRecordStartRecording: "Start recording",
				uploadOrRecordStopRecording: "Stop recording",
				uploadOrRecordCapture: "Capture (3s countdown)",
				uploadOrRecordRetake: "Retake",
				uploadOrRecordPermissionDenied:
					"Permission denied — switch to Upload to provide a file instead.",
				uploadOrRecordUnsupported:
					"Your browser does not support this capture mode — switch to Upload.",
				uploadError: "Could not upload — please try again.",
				validationRequired: "This field is required.",
			},
		},
		toast: {
			info: "Info",
			success: "Success",
			warning: "Warning",
			error: "Error",
		},
	},
	kiosk: {
		dismissInfoButton: "Ok, got it",
		overviewInfoMessage:
			"Watch an example presentation or start to create your own presentation.",
		createPresentationInfoMessage:
			"Start by typing a prompt. This will be both the topic and the title of your presentation.",
		languageChoice: "Please choose your preferred language.",
		impress: {
			title: "About this showcase",
			infoText:
				"AI Slides is a program from TNG Technology Consulting GmbH. It uses artificial intelligence to create an entire presentation from a user input. Many different AI technologies work together for this purpose.\n" +
				"\n" +
				"It was originally an exhibit that was exhibited at the Deutsches Museum in Bonn. For the FutureLab, the program was adapted and further developed in cooperation between TNG and FG-160.",
			contact: {
				title: "Get in contact with the experts",
				internal: "Internal",
				external: "External",
			},
			footer: "The Future Lab is a digital innovation project",
		},
		infoFooter:
			"AI Slides was developed by TNG Technology Consulting GmbH in cooperation with BMW",
		qna: {
			title: "Ask a question about the presentation",
			startRecording: "Start recording",
			stopRecording: "Stop recording",
			error: "Sorry, the question cannot be answered",
		},
	},
};
export default en;
